import store from "../../../../../../../../store";
import {setWizardSelectedValues} from "../../../../../../../../actions/wizardFunctions";
import {getCurrentLayout} from "../../../../../../../appLayout";
import {
  SQFT_PARENT_FEATURE_GROUP_PART_IDS,
  SHAPE_PART_ID,
} from "../../../../../../constants";
import {
  getAvailableSteps,
  getCurrentWizardNavItems,
  getFeatureGroupFromWizardGroup,
} from "../../../../../../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions";
import {
  selectedWizardGroupIsNotInCurrentStepAnymore
} from "../../../../../../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions2";
import {
  BOTTOM_CABINET_GROUP_NAME,
  getCurrentBottomCabinetWizardGroup,
  getEquivalentBottomCabinetFeature,
  getLastWizardSelectedFeatureByPartId,
  getMatchingBottomWizardNavItem,
  getWizardSelectedFeaturesByPartId,
} from "./bottomCabinetWizardMatcher";
import {applyWizardPropsToFeature} from "../../../../../../helpers/wizardFeaturePropsHelper";
import CabinetSwitchers from "../../../../../../../kvComponent/navigation/cabinetSwitchers";

const MAX_WIZARD_STEPS_TO_RESOLVE = 50;
const BATHROOM_CABINET_COLOR_SOURCE_PART_ID = SHAPE_PART_ID.vanity_cabinet;
const BATHROOM_CABINET_INHERITED_COLOR_PROPERTY = "sqftInheritedColorSourcePartId";
const BATHROOM_CABINET_COLOR_FALLBACK_PART_IDS = [
  SHAPE_PART_ID.top_cabinet,
  SHAPE_PART_ID.linen_vanity_cabinet,
];

/**
 * Bottom cabinet SQFT drop flow:
 * getResolvedCabinetParentFeature
 *   -> getMatchingBottomCabinetFeatureFromTop
 *   -> activateFeatureInWizard
 *   -> buildWizardValuesThroughFeature
 *
 * The matcher module owns SKU/profile/color matching. This file owns when we
 * replay wizard state and when we apply the resolved feature to all walls.
 */

const getActiveTopCabinetFeature = (layout) => {
  const topCabinetFeatureGroup = layout.getFeatureGroupByPartId(SHAPE_PART_ID.top_cabinet);
  return topCabinetFeatureGroup?.activeFeature;
};

const getFeatureMetadataValues = (feature, propertyName) => {
  const value = feature?.metaData?.[propertyName];

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  return value ? [value] : [];
};

const normalizeFeatureTerm = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/\s+/g, " ");

const getFeatureSearchValues = (feature) => {
  const filterGroups = store.getState().filterState.filterGroups;

  return [
    feature?.partName,
    feature?.dbFeature?.name,
    feature?.dbFeature?.sharedFeatureName,
    feature?.getFeatureName?.(),
    feature?.getSplittedFeatureName?.(),
    feature?.getColorName?.(filterGroups),
    ...getFeatureMetadataValues(feature, "ActualDisplayedName"),
    ...getFeatureMetadataValues(feature, "MatchingFeature"),
  ].map(normalizeFeatureTerm).filter(Boolean);
};

const featureMatchesTerms = (feature, terms) => {
  const normalizedTerms = terms.map(normalizeFeatureTerm).filter(Boolean);

  if (!normalizedTerms.length) {
    return true;
  }

  const featureValues = getFeatureSearchValues(feature);
  return normalizedTerms.some(term => {
    return featureValues.some(value => value.includes(term) || term.includes(value));
  });
};

const getSelectedCabinetStyleTerms = (wizardSelectedValues, partId) => {
  const selectedFeatures = getWizardSelectedFeaturesByPartId(wizardSelectedValues, partId);

  return selectedFeatures.reduce((terms, feature) => {
    const displayedNames = getFeatureMetadataValues(feature, "ActualDisplayedName");
    terms.push(displayedNames[0], feature?.getFeatureName?.());
    return terms;
  }, []).filter(Boolean);
};

const getVanityColorTerms = (vanityFeature) => {
  const displayedNames = getFeatureMetadataValues(vanityFeature, "ActualDisplayedName");
  const filterGroups = store.getState().filterState.filterGroups;

  return [
    vanityFeature?.getColorName?.(filterGroups),
    ...getFeatureMetadataValues(vanityFeature, "MatchingFeature"),
    ...displayedNames.slice(1),
  ].filter(Boolean);
};

const getCabinetSwitcherName = (feature) => {
  const featureName = feature?.getSplittedFeatureName?.();

  return featureName
    ? featureName.replace(`${feature.dbFeature.groupName} - `, "")
    : null;
};

/**
 * Bathroom upper and linen cabinet wizard groups currently select a cabinet
 * style but do not always expose a color step. In that case their concrete
 * parent SKU inherits Vanity Cabinet color while retaining its own wizard style.
 */
const getBathroomCabinetFeatureFromVanity = ({
  featureGroup,
  partId,
  vanityFeature,
  wizardSelectedValues,
}) => {
  if (!featureGroup?.features?.length || !vanityFeature) {
    return null;
  }

  const styleTerms = getSelectedCabinetStyleTerms(wizardSelectedValues, partId);
  const colorTerms = getVanityColorTerms(vanityFeature);
  const matchingStyleAndColor = (styleTerms.length || colorTerms.length)
    ? featureGroup.features.find(feature => {
      return featureMatchesTerms(feature, styleTerms) && featureMatchesTerms(feature, colorTerms);
    })
    : null;

  if (matchingStyleAndColor) {
    return matchingStyleAndColor;
  }

  const cabinetSwitcherName = getCabinetSwitcherName(vanityFeature);
  return cabinetSwitcherName
    ? CabinetSwitchers.getNeededFeatureFromNavItems(cabinetSwitcherName, featureGroup)
    : null;
};

const getActiveFeatureFromGroup = (featureGroup) => {
  const inheritedFeatureId = featureGroup?.[BATHROOM_CABINET_INHERITED_COLOR_PROPERTY]?.featureId;
  const inheritedFeature = [
    featureGroup?.activeNotInVisualizerFeature,
    featureGroup?.activeFeature,
  ].find(feature => feature?.dbFeature?.id === inheritedFeatureId);

  if (inheritedFeature) {
    return inheritedFeature;
  }

  return featureGroup?.activeFeature || featureGroup?.activeNotInVisualizerFeature || null;
};

const featureHasCabinetBoxes = (feature) => {
  const relatedFeatures = feature?.getFeatureRelatedFeatures?.();
  return Array.isArray(relatedFeatures) && relatedFeatures.length > 0;
};

const getDefaultCabinetParentFeature = (featureGroup, layout) => {
  const currentLayoutName = layout.getCurrentLayoutName();
  const currentLayoutFeatures = featureGroup.featuresList?.[currentLayoutName] || [];
  const fallbackFeatures = currentLayoutFeatures.length
    ? currentLayoutFeatures
    : Object.values(featureGroup.featuresList || {}).flat();

  return fallbackFeatures.find(featureHasCabinetBoxes) || fallbackFeatures[0] || null;
};

const groupUsesInheritedVanityColor = (featureGroup) => {
  const inheritedColorState = featureGroup?.[BATHROOM_CABINET_INHERITED_COLOR_PROPERTY];
  const activeFeature = getActiveFeatureFromGroup(featureGroup);

  return inheritedColorState?.sourcePartId === BATHROOM_CABINET_COLOR_SOURCE_PART_ID
    && inheritedColorState.featureId === activeFeature?.dbFeature?.id;
};

export const clearInheritedBathroomCabinetColor = (featureGroup) => {
  if (featureGroup) {
    delete featureGroup[BATHROOM_CABINET_INHERITED_COLOR_PROPERTY];
  }
};

const setFeatureGroupActiveFeature = (featureGroup, feature) => {
  if (feature.notAvailableInVisualizer) {
    featureGroup.activeNotInVisualizerFeature = feature;
  } else {
    featureGroup.activeFeature = feature;
    featureGroup.activeNotInVisualizerFeature = null;
  }
};

/**
 * Keeps bathroom wall and linen cabinet colors synchronized with Vanity Cabinet
 * while retaining the style selected for each cabinet group.
 */
export const syncBathroomCabinetParentFeaturesFromVanity = ({
  layout = getCurrentLayout(),
  vanityFeature,
  wizardSelectedValues = store.getState().wizardState.wizardSelectedValues,
}) => {
  if (!vanityFeature || vanityFeature.partId !== BATHROOM_CABINET_COLOR_SOURCE_PART_ID) {
    return [];
  }

  return BATHROOM_CABINET_COLOR_FALLBACK_PART_IDS.reduce((features, partId) => {
    const featureGroup = layout.getFeatureGroupByPartId(partId);

    if (!featureGroup) {
      return features;
    }

    const inheritedFeature = getBathroomCabinetFeatureFromVanity({
      featureGroup,
      partId,
      vanityFeature,
      wizardSelectedValues,
    });

    if (!inheritedFeature) {
      console.warn("[SQFT cabinet color] Matching bathroom parent feature was not found", {
        sourceFeatureId: vanityFeature.dbFeature?.id,
        targetPartId: partId,
      });
    }

    if (inheritedFeature) {
      setFeatureGroupActiveFeature(featureGroup, inheritedFeature);
      featureGroup[BATHROOM_CABINET_INHERITED_COLOR_PROPERTY] = {
        sourcePartId: BATHROOM_CABINET_COLOR_SOURCE_PART_ID,
        featureId: inheritedFeature.dbFeature.id,
      };
      features.push(inheritedFeature);
    }

    return features;
  }, []);
};

const getResolvedBathroomCabinetParentFeature = ({
  featureGroup,
  parentFeature,
  partId,
  wizardSelectedValues,
  layout,
}) => {
  if (!BATHROOM_CABINET_COLOR_FALLBACK_PART_IDS.includes(partId)) {
    return parentFeature;
  }

  if (parentFeature
    && !groupUsesInheritedVanityColor(featureGroup)
    && featureHasCabinetBoxes(parentFeature)) {
    return parentFeature;
  }

  const vanityFeatureGroup = layout.getFeatureGroupByPartId(BATHROOM_CABINET_COLOR_SOURCE_PART_ID);
  return getBathroomCabinetFeatureFromVanity({
    featureGroup,
    partId,
    vanityFeature: getActiveFeatureFromGroup(vanityFeatureGroup),
    wizardSelectedValues,
  }) || parentFeature;
};

const getAutoSkippedWizardValue = (wizardGroup) => ({
  wizardGroup,
  isAutoSkippedStep: true,
});

const getSelectedWizardFeatureValue = (wizardGroup, navItem, navItems) => ({
  wizardGroup,
  selectedFeature: navItem.part,
  partId: navItem.part.partId,
  singleFeature: navItems.length === 1,
});

const getWizardPropsFromSelectedValues = (wizardValues, wizardItem, partId) => {
  const selectedStepIndex = Object.keys(wizardValues)
    .map(stepIndex => parseInt(stepIndex, 10))
    .filter(stepIndex => {
      return Number.isInteger(stepIndex)
        && wizardValues[stepIndex]?.partId === partId
        && wizardValues[stepIndex]?.selectedFeature;
    })
    .sort((a, b) => b - a)[0];

  if (!Number.isInteger(selectedStepIndex)) {
    return null;
  }

  const selectedWizardValue = wizardValues[selectedStepIndex];

  return {
    fromWizardId: wizardItem?.id,
    fromWizardStep: selectedStepIndex,
    fromWizardGroupId: selectedWizardValue.wizardGroup?.id,
  };
};

/**
 * Replays only the wizard path needed to select Bottom Cabinet.
 *
 * Business rule: SQFT can add a concrete lower cabinet SKU while the Renuit
 * wizard may still be before lower cabinets. We auto-skip only the required
 * previous steps, select matching Bottom Cabinet profile/color, then stop so
 * unrelated later wizard groups are not touched.
 */
const buildWizardValuesThroughFeature = (layout, feature) => {
  const {wizardState} = store.getState();

  if (!wizardState.wizardIsActive || !wizardState.wizardActiveItem || !feature) {
    return null;
  }

  const wizardItem = wizardState.wizardActiveItem;
  const selectedValues = Object.assign({}, wizardState.wizardSelectedValues);
  let hasSelectedBottomCabinetStep = false;

  for (let stepIndex = 0; stepIndex < MAX_WIZARD_STEPS_TO_RESOLVE; stepIndex++) {
    const wizardSteps = getAvailableSteps(wizardItem, selectedValues, true);
    const wizardStep = wizardSteps[stepIndex];
    let wizardGroup = wizardStep?.stepGroups?.[0];

    if (!wizardGroup) {
      break;
    }

    if (wizardGroup.groupName !== BOTTOM_CABINET_GROUP_NAME) {
      if (hasSelectedBottomCabinetStep) {
        return selectedValues;
      }

      if (!selectedValues[stepIndex]
        || selectedWizardGroupIsNotInCurrentStepAnymore(selectedValues[stepIndex], wizardStep.stepGroups)) {
        selectedValues[stepIndex] = getAutoSkippedWizardValue(wizardGroup);
      }
      continue;
    }

    wizardGroup = getCurrentBottomCabinetWizardGroup(wizardStep, feature);

    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup, layout);
    const navItems = getCurrentWizardNavItems(
      selectedValues,
      stepIndex,
      wizardItem,
      wizardGroup,
      featureGroup
    );
    const matchingNavItem = getMatchingBottomWizardNavItem(layout, navItems, wizardGroup, feature, selectedValues);

    if (matchingNavItem?.part) {
      selectedValues[stepIndex] = getSelectedWizardFeatureValue(wizardGroup, matchingNavItem, navItems);
      hasSelectedBottomCabinetStep = true;
    } else if (!selectedValues[stepIndex]) {
      selectedValues[stepIndex] = getAutoSkippedWizardValue(wizardGroup);
    }
  }

  return hasSelectedBottomCabinetStep ? selectedValues : null;
};

/**
 * Applies the resolved Bottom Cabinet feature both to wizardSelectedValues and
 * to the visualizer feature state. Wizard state is dispatched first because
 * clickFromAllWalls can synchronously re-enter resolver-driven logic.
 */
const activateFeatureInWizard = (layout, feature) => {
  const wizardValues = buildWizardValuesThroughFeature(layout, feature);
  if (wizardValues) {
    applyWizardPropsToFeature(
      feature,
      getWizardPropsFromSelectedValues(
        wizardValues,
        store.getState().wizardState.wizardActiveItem,
        SHAPE_PART_ID.bottom_cabinet
      )
    );

    store.dispatch(setWizardSelectedValues(
      wizardValues,
      true,
      store.getState().wizardState.wizardActiveItem
    ));
  }

  if (typeof feature.clickFromAllWalls === "function") {
    feature.clickFromAllWalls(true);
  }

  return feature;
};

/**
 * When Bottom Cabinet has not been explicitly selected, inherit it from the
 * active Top Cabinet. This keeps lower cabinet boxes aligned with the selected
 * cabinet style/color instead of using a stale bottom activeFeature.
 */
const getMatchingBottomCabinetFeatureFromTop = (layout) => {
  const bottomCabinetFeatureGroup = layout.getFeatureGroupByPartId(SHAPE_PART_ID.bottom_cabinet);
  const topCabinetFeature = getActiveTopCabinetFeature(layout);

  if (!bottomCabinetFeatureGroup?.features || !topCabinetFeature) {
    return null;
  }

  const matchingBottomCabinetFeature = getEquivalentBottomCabinetFeature(layout, topCabinetFeature);

  return matchingBottomCabinetFeature
    ? activateFeatureInWizard(layout, matchingBottomCabinetFeature)
    : null;
};

/**
 * Resolves the wizard parent feature used by SQFT cabinet-box shapes.
 *
 * Lower cabinet boxes normally depend on the Bottom Cabinet wizard feature, but
 * that step can be skipped when it is not required. In that case the lower box
 * should still use the Bottom Cabinet feature that matches the selected Top
 * Cabinet style/color, instead of falling back to a stale or default Bottom
 * Cabinet activeFeature. If Bottom Cabinet was explicitly selected in the
 * wizard, the last selected wizard feature wins because split profile/color
 * steps can share the same partId.
 */
export const getResolvedCabinetParentFeature = ({
  feature,
  parentFeature,
  partId,
  wizardSelectedValues = store.getState().wizardState.wizardSelectedValues,
  layout = getCurrentLayout(),
}) => {
  const currentParentPartId = partId
    || SQFT_PARENT_FEATURE_GROUP_PART_IDS[feature?.partId]
    || parentFeature?.partId;

  const currentFeatureGroup = layout.getFeatureGroupByPartId(currentParentPartId);
  const bathroomParentFeature = getResolvedBathroomCabinetParentFeature({
    featureGroup: currentFeatureGroup,
    parentFeature,
    partId: currentParentPartId,
    wizardSelectedValues,
    layout,
  });

  if (currentParentPartId !== SHAPE_PART_ID.bottom_cabinet) {
    return bathroomParentFeature;
  }

  const selectedBottomCabinetFeature = getLastWizardSelectedFeatureByPartId(
    wizardSelectedValues,
    SHAPE_PART_ID.bottom_cabinet
  );

  if (selectedBottomCabinetFeature) {
    return selectedBottomCabinetFeature;
  }

  return getMatchingBottomCabinetFeatureFromTop(layout) || parentFeature;
};

/**
 * Resolves the initial parent used by the related-items menu before applying
 * cabinet-specific wizard fallbacks. Keeping this policy outside React makes
 * the loading and business priorities explicit and independently testable.
 */
export const getSqftRelatedItemsParentFeature = ({
  featureGroup,
  partId,
  selectedFeature,
  wizardSelectedValues = store.getState().wizardState.wizardSelectedValues,
  layout = getCurrentLayout(),
}) => {
  if (!featureGroup) {
    return null;
  }

  const selectedParentFeature = selectedFeature?.partId === partId
    ? selectedFeature
    : null;
  let groupActiveFeature = getActiveFeatureFromGroup(featureGroup);

  if (!selectedParentFeature && !groupActiveFeature && !featureGroup.allFeaturesReceived) {
    return null;
  }

  if (!selectedParentFeature && !groupActiveFeature) {
    featureGroup.setDefault(layout, true);
    groupActiveFeature = getActiveFeatureFromGroup(featureGroup);

    if (!groupActiveFeature) {
      groupActiveFeature = getDefaultCabinetParentFeature(featureGroup, layout);
      if (groupActiveFeature) {
        setFeatureGroupActiveFeature(featureGroup, groupActiveFeature);
      }
    }
  }

  return getResolvedCabinetParentFeature({
    parentFeature: selectedParentFeature || groupActiveFeature,
    partId,
    wizardSelectedValues,
    layout,
  });
};
